'use client';

import { useCallback } from 'react';

import type { PostCommentSearchItem } from '@ps/api/types';
import { useAsyncPagination } from '@ps/functions/hooks/useAsyncPagination';
import { proxyFetch } from '@ps/functions/utils/proxyFetch';

/**
 * Fetches top-level comments for a post. Mirrors legacy
 * `useFetchPostComments` in
 * `flows/bio/wall/functions/fetchPostComments.ts`:
 *   - `rootComment: true` so replies don't pollute the
 *     top-level list (replies are loaded on-demand per
 *     comment via the same endpoint with `parentId`).
 *   - Newest-first ordering matches the legacy feed.
 *
 * Returns the standard `useAsyncPagination` shape so the
 * comments panel can wire a "Load more" button identically
 * to the My Feed tab.
 */
export function usePostComments(sellerId: string, postId: string) {
  const loadData = useCallback(
    async (page: number, pageSize: number) => {
      if (!sellerId || !postId) {
        return {
          data: [] as PostCommentSearchItem[],
          total: 0,
          hasMore: false,
        };
      }
      const params = new URLSearchParams({
        creatorId: sellerId,
        postId,
        pageNo: String(page),
        pageSize: String(pageSize),
      });
      const response = await proxyFetch<{
        rows: PostCommentSearchItem[];
        total: number;
      }>(`/api/bio/posts/comments?${params.toString()}`);
      const rows = response.rows;
      return {
        data: rows,
        total: response.total,
        hasMore: rows.length >= pageSize,
      };
    },
    [sellerId, postId],
  );

  return useAsyncPagination<PostCommentSearchItem>(loadData, { pageSize: 20 });
}
